/**
 * Bandeau céleste en tête de page admin.
 * Frise horizontale ~80px : écliptique + 12 signes zodiacaux + dust stars
 * + ornements de bordures. Fait écho à la carte céleste de fond, mais
 * dans une "vue rapprochée" centrée sur la bande zodiacale.
 *
 * Light mode : trace sépia ornementale (~18% opacité).
 * Dark mode  : bande révélée (~45% opacité), dust qui scintille.
 */
export function CelestialBanner() {
  return (
    <div className="mf-celestial-banner" aria-hidden="true">
      <svg
        viewBox="0 0 1600 80"
        preserveAspectRatio="xMidYMid slice"
        className="mf-celestial-banner__svg"
      >
        {/* Bordures décoratives haut + bas */}
        <line
          x1="0"
          y1="2"
          x2="1600"
          y2="2"
          className="mf-celestial-banner__edge"
        />
        <line
          x1="0"
          y1="78"
          x2="1600"
          y2="78"
          className="mf-celestial-banner__edge"
        />

        {/* Écliptique (légère ondulation) */}
        <path
          d="M 0 40 Q 400 30, 800 40 T 1600 40"
          fill="none"
          className="mf-celestial-banner__ecliptic"
        />

        {/* 12 signes zodiacaux */}
        {ZODIAC.map((z) => (
          <g key={z.symbol}>
            <text
              x={z.x}
              y={z.y - 5}
              className="mf-celestial-banner__symbol"
            >
              {z.symbol}
            </text>
            <text
              x={z.x}
              y={z.y + 12}
              className="mf-celestial-banner__name"
            >
              {z.name}
            </text>
            <circle
              cx={z.x}
              cy={z.y + 22}
              r="1.4"
              className="mf-celestial-banner__anchor"
            />
          </g>
        ))}

        {/* Dust stars dispersées dans la bande */}
        {DUST.map(([x, y, r], i) => (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={r}
            style={{ animationDelay: `${(i % 7) * 0.4}s` }}
            className="mf-celestial-banner__dust"
          />
        ))}

        {/* Ornement gauche : compass rose stylisée */}
        <g className="mf-celestial-banner__ornament">
          <circle cx="22" cy="40" r="9" fill="none" />
          <line x1="22" y1="28" x2="22" y2="52" />
          <line x1="10" y1="40" x2="34" y2="40" />
          <line x1="14" y1="32" x2="30" y2="48" />
          <line x1="14" y1="48" x2="30" y2="32" />
        </g>

        {/* Ornement droit */}
        <g
          className="mf-celestial-banner__ornament"
          style={{ transform: "translateX(1556px)" }}
        >
          <circle cx="22" cy="40" r="9" fill="none" />
          <line x1="22" y1="28" x2="22" y2="52" />
          <line x1="10" y1="40" x2="34" y2="40" />
          <line x1="14" y1="32" x2="30" y2="48" />
          <line x1="14" y1="48" x2="30" y2="32" />
        </g>
      </svg>
    </div>
  );
}

const ZODIAC = [
  { symbol: "♈", name: "ARIES",       x: 130,  y: 40 },
  { symbol: "♉", name: "TAURUS",      x: 250,  y: 38 },
  { symbol: "♊", name: "GEMINI",      x: 370,  y: 36 },
  { symbol: "♋", name: "CANCER",      x: 490,  y: 34 },
  { symbol: "♌", name: "LEO",         x: 610,  y: 35 },
  { symbol: "♍", name: "VIRGO",       x: 730,  y: 37 },
  { symbol: "♎", name: "LIBRA",       x: 850,  y: 40 },
  { symbol: "♏", name: "SCORPIUS",    x: 970,  y: 43 },
  { symbol: "♐", name: "SAGITTARIUS", x: 1090, y: 45 },
  { symbol: "♑", name: "CAPRICORNUS", x: 1210, y: 46 },
  { symbol: "♒", name: "AQUARIUS",    x: 1330, y: 45 },
  { symbol: "♓", name: "PISCES",      x: 1450, y: 43 },
];

const DUST: ReadonlyArray<[number, number, number]> = [
  [70, 14, 0.8], [185, 22, 0.9], [310, 12, 1.0], [430, 24, 0.7],
  [555, 14, 0.9], [680, 22, 0.8], [800, 12, 1.0], [925, 24, 0.7],
  [1045, 14, 0.9], [1170, 22, 1.0], [1290, 12, 0.8], [1410, 24, 0.9],
  [1535, 16, 0.8],

  [80, 62, 0.9], [205, 68, 0.8], [325, 58, 1.0], [445, 65, 0.7],
  [575, 60, 0.9], [700, 68, 0.8], [820, 58, 1.0], [945, 65, 0.7],
  [1065, 60, 0.9], [1190, 68, 0.8], [1305, 58, 1.0], [1425, 65, 0.7],
];
